// IMPORTANT: BaseHelpPopup is only for internal use.
// Consumer should only be using HelpPopup
import { createElement, useState, Fragment, useRef } from 'react';
import { Button, ButtonVariant } from '../../Button';
import useTextLocalizer from '../../../locale/useTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { HelpPopupContent } from '.';
import { HelpButton, HelpButtonToolbar } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-HelpPopup-gui-icu.properties';
import { Popover } from '../../Popover/Popover';
import { setRef } from '../../../util/ref-util';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { getMenuButtonTokens } from '../../MenuButton/__internal__/BaseMenuButton';
import { BaseHelpPopupProps } from './';

import cn from './BaseHelpPopup.css.json';
import './BaseHelpPopup.css';

const useHelpTextLocalizer = createUseTextLocalizer(bundle);

export const BaseHelpPopup = (props: BaseHelpPopupProps) => {
  // Help Popup normally controls whether its popover is open using its button, but can be controlled externally with isOpen and setIsOpen
  const [popupOpenInternal, setPopupOpenInternal] = useState(false);
  // If setIsOpen is defined and isOpen has been passed in, use it to control the open status, otherwise use the value that is tracked internally
  const popupOpen =
    props.isOpen !== undefined && props.setIsOpen !== undefined ? props.isOpen : popupOpenInternal;

  const icon = props.variant === 'toolbar' ? HelpButtonToolbar : HelpButton;
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      pd: dt.spacing.core.m,
      min: dt.spacing.size.xxs,
      'font-size-l': dt.fontNova.size.l,
    },
    'help-popup-container'
  );

  const localize = useHelpTextLocalizer();
  const localizeHelpContent = useTextLocalizer(props.library);
  // Localize returns key passed to it if no content is found
  // If that happens, or the content found is blank, set default tooltip and aria text
  const localizedTooltip = localizeHelpContent(`${props.contentKey}.help.tip.txt`);
  const tooltip =
    localizedTooltip && localizedTooltip !== `${props.library}.${props.contentKey}.help.tip.txt`
      ? localizedTooltip
      : localize('default.tooltip.msg');
  const ariaText = localizeHelpContent(`${props.contentKey}.help.aria.txt`);
  let ariaLabel =
    ariaText && ariaText !== `${props.library}.${props.contentKey}.help.aria.txt`
      ? ariaText
      : localize('default.tooltip.msg');
  ariaLabel = props.additionalAriaText ? `${props.additionalAriaText}, ${ariaLabel}` : ariaLabel;

  return (
    <Fragment>
      <Button
        style={{ display: 'inline-flex' }}
        disabled={props.disabled}
        testId={postfixTestId(props.testId, 'button')}
        ref={(element) => {
          buttonRef.current = element;
          setRef(props.buttonRef, element);
        }}
        onPress={() => {
          setPopupOpenInternal(true);
          props.setIsOpen?.(true);
        }}
        icon={{ icon }}
        tooltip={tooltip}
        ariaLabel={ariaLabel}
        variant={ButtonVariant.Icon}
        className={props.className}
        focusable={props.focusable ?? true}
        tokens={() => getMenuButtonTokens(popupOpenInternal, ButtonVariant.Icon, undefined, dt)}
      />
      <Popover
        testId={props.testId}
        referenceElementRef={buttonRef}
        isOpen={popupOpen}
        onClose={() => {
          setPopupOpenInternal(false);
          props.setIsOpen?.(false);
        }}
        ariaLabel={localize('dialog.content.aria')}
        role="dialog"
        style={{ ...tokens, maxWidth: `${dt.spacing.size.l}`, maxHeight: '50vh' }}
        focusable
        className={cn.container}
        placement="auto"
      >
        <HelpPopupContent {...props} />
      </Popover>
    </Fragment>
  );
};
